/**
 * Thin axios wrapper around the admin enrichment-reconciliation endpoints
 * under `/admin/enrichment/pending/*`. Tenant scope + ADMIN role are
 * enforced server-side via the JWT middleware; the view also runs a
 * defense-in-depth role guard so a stale token can't bypass the nav
 * filter.
 *
 * Pattern mirrors `src/api/dataChanges.ts`: bare `client.get/post/patch`
 * + typed return values + async/await; no AxiosResponse objects leak
 * past the module boundary.
 */
import client from '@/api/client';
import type {
  ListPendingEnrichmentsParams,
  PendingEnrichmentListResponse,
  PendingEnrichmentNotesPatch,
  PendingEnrichmentRead,
  ResolveEnrichmentRequest,
} from '@/types/enrichmentReconcile';

const BASE = '/admin/enrichment/pending';

/**
 * GET /admin/enrichment/pending
 *
 * Defaults match the backend: `tier='all'`, `reconciled='false'`,
 * `limit=50`, `offset=0`. Pass `reconciled='all'` to include resolved
 * rows alongside the queue.
 */
export async function fetchPendingEnrichments(
  params: ListPendingEnrichmentsParams = {},
): Promise<PendingEnrichmentListResponse> {
  const query: Record<string, string | number> = {};
  if (params.tier) query.tier = params.tier;
  if (params.reconciled) query.reconciled = params.reconciled;
  if (params.import_batch_id) query.import_batch_id = params.import_batch_id;
  if (params.limit != null) query.limit = params.limit;
  if (params.offset != null) query.offset = params.offset;

  const res = await client.get<PendingEnrichmentListResponse>(BASE, {
    params: query,
  });
  return res.data;
}

/**
 * GET /admin/enrichment/pending/{id}
 *
 * Returns one pending row with `candidates` hydrated (name, address,
 * birth_year, party) so the reconcile modal can render a pick-list
 * without a second round-trip.
 */
export async function fetchPendingEnrichment(
  id: number,
): Promise<PendingEnrichmentRead> {
  const res = await client.get<PendingEnrichmentRead>(`${BASE}/${id}`);
  return res.data;
}

/**
 * POST /admin/enrichment/pending/{id}/resolve
 *
 * Server returns 204 No Content; we resolve to void. Callers should
 * remove the row from their local list optimistically and re-fetch the
 * total only if the user navigates back to the list.
 */
export async function resolvePendingEnrichment(
  id: number,
  body: ResolveEnrichmentRequest,
): Promise<void> {
  await client.post(`${BASE}/${id}/resolve`, body);
}

/**
 * PATCH /admin/enrichment/pending/{id}
 *
 * Update reconciliation_notes only. Returns the updated row (no
 * hydrated candidates).
 */
export async function patchPendingEnrichmentNotes(
  id: number,
  body: PendingEnrichmentNotesPatch,
): Promise<PendingEnrichmentRead> {
  const res = await client.patch<PendingEnrichmentRead>(`${BASE}/${id}`, body);
  return res.data;
}
